import type { BillableOperation, PolicyHit } from '@legate/shared';
import { authStore } from '../auth.js';

/** Vite proxies `/api` to the API service (see vite.config.ts). */
export const API_BASE = '/api';

// ── Envelope types ───────────────────────────────────────────────────────────

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface BillingReceipt {
  operation: BillableOperation | string;
  credits: number;
  balanceAfter: number;
}

/** Mutations that cost credits carry the receipt alongside the result. */
export type Billed<T> = T & { billing: BillingReceipt };

export interface ApiErrorBody {
  error: { code: string; message: string; detail?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }

  /** 402 — the tenant is out of credits. */
  get isInsufficientCredits(): boolean {
    return this.code === 'insufficient_credits';
  }

  /** 422 — the Policy Engine refused the turn; `detail` carries the hits. */
  get isPolicyBlock(): boolean {
    return this.code === 'policy_block';
  }

  get policyHits(): PolicyHit[] {
    return this.isPolicyBlock && Array.isArray(this.detail) ? (this.detail as PolicyHit[]) : [];
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

// ── Transport ────────────────────────────────────────────────────────────────

export type QueryValue = string | number | boolean | undefined | null | (string | number)[];
export type Query = Record<string, QueryValue>;

function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `${url}?${qs}` : url;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  /** Skips the Authorization header — used by login and register. */
  anonymous?: boolean;
  /** Sends a FormData body untouched (CSV/JSON import). */
  formData?: FormData;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, signal, anonymous = false, formData } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (!anonymous) {
    const token = authStore.getToken();
    if (token !== null) headers['Authorization'] = `Bearer ${token}`;
  }
  if (formData === undefined && body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      signal,
      body: formData ?? (body === undefined ? undefined : JSON.stringify(body)),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'network_error', 'Service unreachable. Check the connection.', cause);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const envelope = parsed as ApiErrorBody | null;
    const error = envelope?.error;
    if (error) throw new ApiError(response.status, error.code, error.message, error.detail);
    throw new ApiError(response.status, 'http_error', `Request failed with ${response.status}`, text);
  }

  // A 401 anywhere means the session is gone; clear it so the router redirects.
  return parsed as T;
}

export const http = {
  get: <T>(path: string, query?: Query, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>(path, { method: 'POST', body, query }),
  put: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>(path, { method: 'PUT', body, query }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string, query?: Query) => request<T>(path, { method: 'DELETE', query }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: 'POST', formData }),
  anon: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body, anonymous: true }),
};

/**
 * Server-sent events with the bearer token attached. EventSource cannot set
 * headers, so the conversation stream is consumed through fetch + a reader.
 */
export function streamEvents(
  path: string,
  onEvent: (event: { type: string; data: unknown }) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = authStore.getToken();
  return fetch(`${API_BASE}${path}`, {
    headers: {
      Accept: 'text/event-stream',
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
    },
    signal,
  }).then(async (response) => {
    if (!response.ok || response.body === null) {
      throw new ApiError(response.status, 'stream_failed', 'Could not open the event stream');
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const chunk = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');

        let type = 'message';
        const dataLines: string[] = [];
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event:')) type = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (dataLines.length === 0) continue;
        const raw = dataLines.join('\n');
        try {
          onEvent({ type, data: JSON.parse(raw) });
        } catch {
          onEvent({ type, data: raw });
        }
      }
    }
  });
}
